import { useEffect, useState } from "react";
import { BUILD_INFO } from "../../shared/build-info";
import {
  Badge,
  ComponentDemoState,
  EmptyState,
  ErrorState,
  Skeleton,
  Table,
} from "../components/ui";

interface Health {
  status: string;
  database: {
    status: string;
    schemaVersion: string | null;
  };
  requestId: string;
}

export function HomePage() {
  const [health, setHealth] = useState<Health | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/v1/foundation", { signal: controller.signal })
      .then(async (response) => {
        const body = (await response.json()) as Health & { error?: { requestId?: string } };
        if (!response.ok) {
          throw new Error(body.error?.requestId ?? body.requestId ?? "unknown");
        }
        setHealth(body);
      })
      .catch((reason: unknown) => {
        if (reason instanceof Error && reason.name !== "AbortError") {
          setError(reason.message);
        }
      });
    return () => controller.abort();
  }, []);

  return (
    <>
      <section className="hero-panel">
        <div>
          <p className="eyebrow">WorkingDog VN Foundation</p>
          <h1>Nền tảng dữ liệu đáng tin cậy cho cộng đồng working dog Việt Nam.</h1>
          <p>
            PHẦN 01 thiết lập app shell, design system, route boundaries và pipeline Cloudflare.
            Chưa tạo dữ liệu chó giả hoặc giả vờ nghiệp vụ đã hoàn thiện.
          </p>
        </div>
        <div className="hero-meta">
          <Badge tone="success">Foundation active</Badge>
          <code>{BUILD_INFO.id.slice(0, 12)}</code>
        </div>
      </section>
      <section className="dashboard-grid">
        <article className="panel">
          <p className="eyebrow">Kết nối nền tảng</p>
          {error ? (
            <ErrorState
              title="Backend chưa sẵn sàng"
              description="Kiểm tra Pages Functions hoặc Worker binding và D1 migration."
              requestId={error}
            />
          ) : health ? (
            <div className="status-summary">
              <Badge tone={health.status === "ok" ? "success" : "warning"}>
                {health.status}
              </Badge>
              <h2>D1 schema {health.database.schemaVersion ?? "chưa xác định"}</h2>
              <p>Request ID: <code>{health.requestId}</code></p>
            </div>
          ) : (
            <Skeleton lines={4} />
          )}
        </article>
        <article className="panel">
          <p className="eyebrow">Các shell đã có</p>
          <Table
            caption="Route shells foundation"
            headers={["Khu vực", "Route", "Trạng thái"]}
            rows={[
              ["Public", "/", <Badge key="public" tone="success">Sẵn sàng</Badge>],
              ["Member", "/member", <Badge key="member" tone="info">Shell</Badge>],
              ["Admin", "/admin", <Badge key="admin" tone="info">Shell</Badge>],
            ]}
          />
        </article>
      </section>
    </>
  );
}

export function ComponentCatalogPage() {
  return (
    <section>
      <div className="page-heading">
        <p className="eyebrow">Design system</p>
        <h1>Component nền tảng</h1>
        <p>Tất cả màu, spacing, radius, shadow và trạng thái lấy từ token tập trung.</p>
      </div>
      <div className="panel"><ComponentDemoState /></div>
      <div className="state-grid">
        <EmptyState title="Chưa có dữ liệu" description="Empty state không tự tạo dữ liệu giả." />
        <ErrorState
          title="Không thể tải dữ liệu"
          description="Server error luôn hiển thị request ID khi có."
          requestId="example-request-id"
        />
      </div>
    </section>
  );
}

export function MemberFoundationPage() {
  return (
    <section>
      <div className="page-heading">
        <Badge tone="info">Member shell</Badge>
        <h2>Không gian thành viên đã sẵn sàng để gắn auth.</h2>
        <p>
          Route guard hiện chỉ đánh dấu scope ở server. Prompt 02 sẽ xác thực session, quyền
          và dữ liệu thật.
        </p>
      </div>
      <EmptyState
        title="Chưa có module nghiệp vụ"
        description="Hồ sơ chó, ownership và tài liệu sẽ được thêm bằng các PR riêng."
      />
    </section>
  );
}

export function AdminFoundationPage() {
  return (
    <section>
      <div className="page-heading">
        <Badge tone="warning">Admin foundation</Badge>
        <h2>Kiểm soát phiên bản và trạng thái hệ thống</h2>
        <p>Khu vực này chưa cho phép mutation. Prompt 02 sẽ bổ sung auth/RBAC và audit bắt buộc.</p>
      </div>
      <Table
        caption="Thông tin build"
        headers={["Thuộc tính", "Giá trị"]}
        rows={[
          ["Version", BUILD_INFO.version],
          ["Build ID", <code key="id">{BUILD_INFO.id}</code>],
          ["Build time", BUILD_INFO.timestamp],
          ["Locale", "vi-VN"],
          ["Timezone", "Asia/Ho_Chi_Minh"],
        ]}
      />
    </section>
  );
}

export function LoadingPage() {
  return <main className="centered-page"><div className="panel"><h1>Đang tải nền tảng</h1><Skeleton lines={5} /></div></main>;
}

export function ErrorPage({ status }: { status: 403 | 500 }) {
  const requestId = new URLSearchParams(window.location.search).get("requestId") ?? undefined;
  return (
    <main className="centered-page">
      <ErrorState
        title={status === 403 ? "Không có quyền truy cập" : "Lỗi máy chủ"}
        description={
          status === 403
            ? "Tài khoản hiện tại không có quyền cho khu vực này."
            : "Hệ thống đang gặp sự cố. Vui lòng thử lại sau."
        }
        requestId={requestId}
      />
    </main>
  );
}

export function NotFoundPage() {
  return (
    <main className="centered-page">
      <ErrorState
        title="Không tìm thấy trang"
        description="Đường dẫn không tồn tại hoặc đã được di chuyển."
        action={{ label: "Về trang chủ", onClick: () => { window.location.href = "/"; } }}
      />
    </main>
  );
}
